import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import DropdownSelect from 'components/V2/DropdownSelect';
import { USER_ENVIRONMENTS } from 'constants';
import { IS_SANDBOX_ENV } from 'helper/constant';
import { startCase } from 'lodash';

import { updateEnvironmentWarningAction } from 'redux/actions/apiCall';
import { selectIsSandboxAdminExist } from 'redux/selectors/auth';

import styles from './switchEnvironment.module.css';

import ChangeEnvironmentWarning from '../ChangeEnvironmentWarning';
import RequestSandboxButton from './RequestSandboxButton';

const { SANDBOX, SANDBOX_DEV, PRODUCTION, CURRENT_ENV, STAGING, ALPHA } = USER_ENVIRONMENTS;

// Deployed non-sandbox envs whose switcher only offers a jump into sandbox.
const HIDE_WHEN_NO_SANDBOX_ADMIN = new Set([PRODUCTION, STAGING, ALPHA]);

function SwitchEnvironment() {
    const environment = process.env.REACT_APP_BUILD_ENV;
    const dispatch = useDispatch();
    const isSandboxAdminExist = useSelector(selectIsSandboxAdminExist);
    const [targetEnv, setTargetEnv] = useState(null);

    const handleEnvChange = (option) => {
        if (option?.value === CURRENT_ENV) return;
        setTargetEnv(option?.value);
        dispatch(updateEnvironmentWarningAction(true));
    };

    // No sandbox admin means there is nothing to switch into from prod/stage/alpha — instead
    // offer a way to request one.
    if (HIDE_WHEN_NO_SANDBOX_ADMIN.has(environment) && !isSandboxAdminExist) {
        return <RequestSandboxButton />;
    }

    const environmentOptions = [CURRENT_ENV];

    if (IS_SANDBOX_ENV) {
        environmentOptions.push(PRODUCTION);
    } else {
        environmentOptions.push(environment === PRODUCTION ? SANDBOX : SANDBOX_DEV);
    }

    // if (environment !== PRODUCTION) {
    //     environmentOptions.push(SANDBOX_LOCAL);
    // }

    const options = environmentOptions.map((env) => ({
        value: env,
        className: styles[env],
    }));

    const renderTrigger = (value) => {
        return (
            <Box className={styles.selected_value}>
                <span className={clsx(styles.activeDot, styles[value])} />
                <Typography className={styles.environmentText}>{startCase(value)}</Typography>
            </Box>
        );
    };

    const renderOption = (option) => (
        <>
            <span className={clsx(styles.activeDot, styles[option.value])} />
            <Typography className={styles.environmentText}>{startCase(option.value)}</Typography>
        </>
    );

    return (
        <>
            <DropdownSelect
                value={environment}
                options={options}
                onChange={handleEnvChange}
                renderTrigger={renderTrigger}
                renderOption={renderOption}
                menuTitle="Switch Environments"
                id="switch-environment-menu"
                showActiveState
            />
            <ChangeEnvironmentWarning targetEnv={targetEnv} />
        </>
    );
}

export default SwitchEnvironment;
